// RigSentinel Context Engine — Phase RCE-1
//
// FIELD CLASSIFICATION
// --------------------
// Every field the engine knows about is tagged with a category that tells the
// engine HOW it should be treated when resolving values and reconciling
// conflicts.
//
//   * `static`        — Identity / never changes during a well (e.g. rigName,
//                       wellName, operator, client).
//   * `semi-static`   — Changes a few times per well (e.g. holeSection,
//                       casingShoeTvdFt, mudWeightInPpg).
//   * `dynamic`       — Changes day-to-day or shift-to-shift (e.g. currentMd,
//                       currentTvd, currentOperation, sicpPsi, sidppPsi).
//   * `document-only` — Belongs only to a specific document and must NEVER be
//                       inherited (e.g. reportNumber, sheetNumber, approvedBy,
//                       printedAt).
//
// Pure data; no side effects. The registry is the single source of truth for
// every consumer in lib/rce/*.

export const FIELD_KINDS = ["static", "semi-static", "dynamic", "document-only"];

// Canonical field paths used across the RCE. Phase RCE-1 ships the
// foundational set; future phases will register their own paths.
//
// NOTE: keep entries alphabetised within each kind for easy diffing.
export const FIELD_REGISTRY = Object.freeze({
  // --- Static (identity) ---------------------------------------------------
  "project.id":                "static",
  "project.name":              "static",
  "project.operator":          "static",
  "project.client":            "static",
  "project.country":           "static",
  "project.field":             "static",
  "project.block":             "static",
  "rig.id":                    "static",
  "rig.name":                  "static",
  "rig.type":                  "static",
  "rig.contractor":            "static",
  "rig.operator":               "static",
  "rig.country":                "static",
  "rig.field":                  "static",
  "rig.block":                  "static",
  // RCE-4.5 — Pump output (default circulating pump bbl/stk).
  "rig.pumpOutputBblStk":      "semi-static",
  "rig.surfaceLineVolBbl":     "semi-static",
  "well.id":                   "static",
  "well.name":                 "static",
  "well.uwi":                  "static",
  "well.environment":          "static",
  "well.bopLocation":          "static",
  "well.spudDate":             "static",

  // --- Semi-static (changes with section / program) -----------------------
  "well.holeSection":          "semi-static",
  "well.holeSizeIn":           "semi-static",
  "well.casingShoeMdFt":       "semi-static",
  "well.casingShoeTvdFt":      "semi-static",
  "well.totalDepthPlannedFt":  "semi-static",
  "well.totalDepthFinalFt":    "semi-static",
  "well.waterDepthFt":         "semi-static",
  // RCE-4.5 — Well geometry derivations (computed from Drill String + BHA + Casing).
  "well.stringInternalVolBbl": "semi-static",
  "well.bitToShoeVolBbl":      "semi-static",
  "well.shoeToSurfaceVolBbl":  "semi-static",
  // RCE-4.5 — LOT / FIT engineering data owned by the Well Profile.
  "well.lotPressurePsi":       "semi-static",
  "well.mwAtLotPpg":           "semi-static",
  "bha.id":                    "semi-static",
  "bha.revision":              "semi-static",
  "drillString.id":            "semi-static",
  "drillString.revision":      "semi-static",
  "mud.weightInPpg":           "semi-static",
  "mud.fluidBasis":            "semi-static",
  // RCE-3 — Drilling Fluid Management additional properties (tri-state lives
  // on the Mud Program / DMR; resolver publishes the "current" leg first).
  "mud.programId":                 "semi-static",
  "mud.plasticViscosityCp":        "semi-static",
  "mud.yieldPointLbf100ft2":       "semi-static",
  "mud.gelStrength10sLbf100ft2":   "semi-static",
  "mud.gelStrength10minLbf100ft2": "semi-static",
  "mud.fluidLossMl30min":          "semi-static",
  "mud.pH":                        "semi-static",
  "mud.sandContentPct":            "semi-static",
  "mud.oilWaterRatio":             "semi-static",
  "mud.electricalStabilityV":      "semi-static",
  "mud.htHpFluidLossMl":           "semi-static",

  // --- Dynamic (changes daily / per-shift) --------------------------------
  "operation.current":         "dynamic",
  "operation.startedAt":       "dynamic",
  "well.currentMdFt":          "dynamic",
  "well.currentTvdFt":         "dynamic",
  "well.currentRotatingHours": "dynamic",
  "killSheet.sidppPsi":        "dynamic",
  "killSheet.sicpPsi":         "dynamic",
  "killSheet.slowPumpPsi":     "dynamic",
  "killSheet.pumpRateSpm":     "dynamic",

  // --- Daily Operations (RCE-2) — owned by lib/dailyOps -------------------
  "dailyOps.reportDate":         "dynamic",
  "dailyOps.reportNumber":       "dynamic",
  "dailyOps.tour":               "dynamic",
  "dailyOps.shift":              "dynamic",
  "dailyOps.preparedBy":         "dynamic",
  "dailyOps.position":           "dynamic",
  "dailyOps.activities":         "dynamic",
  "dailyOps.accomplishments":    "dynamic",
  "dailyOps.problems":           "dynamic",
  "dailyOps.correctiveActions":  "dynamic",
  "dailyOps.currentStatus":      "dynamic",
  "dailyOps.nextPlan":           "dynamic",
  "dailyOps.lastReportDate":     "dynamic",
  "dailyOps.totalFootageDrilled":"dynamic",
  "dailyOps.totalNptHours":      "dynamic",
  "dailyOps.shiftSummary":       "dynamic",
  "dailyOps.tourSummary":        "dynamic",
  "dailyOps.logEntries":         "dynamic",
  "dailyOps.notes":              "dynamic",
  "dailyOps.dayEndSummary":      "dynamic",

  // Drilling Fluid documents (RCE-3) — owned by lib/mud/dmr
  "fluidDoc.reportDate":      "dynamic",
  "fluidDoc.preparedBy":      "dynamic",
  "fluidDoc.volumes":         "dynamic",
  "fluidDoc.treatments":      "dynamic",
  "fluidDoc.solidsControl":   "dynamic",
  "fluidDoc.gainsLosses":     "dynamic",
  "fluidDoc.notes":           "dynamic",
  "fluidDoc.recommendations": "dynamic",

  // --- Document-only (never inherited) ------------------------------------
  "doc.reportNumber":          "document-only",
  "doc.sheetNumber":           "document-only",
  "doc.revisionLetter":        "document-only",
  "doc.preparedBy":            "document-only",
  "doc.approvedBy":            "document-only",
  "doc.approvedAt":            "document-only",
  "doc.printedAt":             "document-only",
  "doc.signature":             "document-only",

  // --- Settings (RCE-4.5) — engineering defaults ---------------------------
  "settings.safetyMarginPpg":         "semi-static",
  "settings.scheduleIntervalStrokes": "semi-static",
  "settings.wellControlStandard":     "static",
});

/**
 * Classify a field path. Returns one of FIELD_KINDS or "unknown" if the
 * field has not been registered yet. The engine treats unknown fields as
 * "document-only" by default so an unregistered field can never silently
 * leak across reports.
 */
export function classifyField(path) {
  if (!path || typeof path !== "string") return "unknown";
  const k = FIELD_REGISTRY[path];
  return k || "unknown";
}

/** A field is inheritable if its classification is static / semi-static /
 *  dynamic. Document-only and unknown fields are NOT inheritable. */
export function isInheritable(path) {
  const k = classifyField(path);
  return k === "static" || k === "semi-static" || k === "dynamic";
}

/** List every registered field of a given kind. */
export function fieldsByKind(kind) {
  return Object.entries(FIELD_REGISTRY)
    .filter(([, v]) => v === kind)
    .map(([k]) => k);
}
